import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { FlexContainer } from '@/ui';
import { AppButton } from '@/ui/buttons';
import { Dialog } from 'primereact/dialog';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const StyledDialog = styled(Dialog)`
  width: 500px;

  .p-dialog-header {
    padding: var(--semi-big-padding) var(--semi-big-padding) 0;
    font-size: var(--modal-heading-font-size);
    font-weight: 700;
    line-height: 1.4;
    color: var(--black-main);
  }

  .p-dialog-content {
    padding: 0 var(--semi-big-padding) var(--semi-big-padding);
  }
`;

const BodyText = styled.p`
  font-size: var(--small-font-size);
  line-height: 1.5;
  color: var(--black-main);
  margin: 0;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 31px;
`;

const ActiveUsersPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--green-pale);
  color: var(--green-dark);
  font-size: var(--xsmall-font-size);
  font-weight: 500;
  padding: var(--xxxsmall-padding) var(--xsmall-padding);
  border-radius: var(--grand-border-radius);
  line-height: 1.5;
  white-space: nowrap;
  vertical-align: middle;
`;

type ScheduleUsersWarningModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onScheduleAnyway: () => void;
  activeUsersCount: number;
};

export const ScheduleUsersWarningModal: React.FC<
  ScheduleUsersWarningModalProps
> = ({ isOpen, onClose, onScheduleAnyway, activeUsersCount }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleAddUsers = () => {
    onClose();
    navigate(RedirectPaths[RedirectPathsEnum.USERS]());
  };

  return (
    <StyledDialog
      visible={isOpen}
      onHide={onClose}
      modal
      draggable={false}
      resizable={false}
      className="v2"
      header={t('phishing.simulations.modal.zeroUsers.heading')}
    >
      <ContentWrapper>
        <div>
          <BodyText>
            {t('phishing.simulations.modal.zeroUsers.message.before')}{' '}
            <ActiveUsersPill>
              {t('phishing.simulations.modal.zeroUsers.activeUsers', {
                count: activeUsersCount,
              })}
            </ActiveUsersPill>
          </BodyText>
          <BodyText className="mt-3">
            {t('phishing.simulations.modal.zeroUsers.message.after')}
          </BodyText>
        </div>

        <FlexContainer justify="flex-start" gap="var(--xxsmall-padding)">
          <AppButton
            label={t('phishing.simulations.modal.zeroUsers.scheduleSimulation')}
            onClick={onScheduleAnyway}
            isDisabled={activeUsersCount === 0}
          />
          <AppButton
            label={t('phishing.simulations.modal.zeroUsers.addUsers')}
            onClick={handleAddUsers}
            severity="secondary"
            type="outlined"
          />
        </FlexContainer>
      </ContentWrapper>
    </StyledDialog>
  );
};
